import { useEffect, useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import {
    IpxCredentialFieldClassification,
    type IpxCredentialFieldDescriptor,
    type IpxCredentialSaveFields,
} from '@pillar-portal/api-types';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { runInBackground } from '@/lib/utils';

export interface IpxCredentialFormInitialValues {
    readonly account_id: string;
    readonly field_values: Readonly<Record<string, string>>;
}

export interface IpxCredentialFormProps {
    readonly fieldDescriptors: readonly IpxCredentialFieldDescriptor[];
    readonly initialValues: IpxCredentialFormInitialValues;
    readonly onSave: (payload: IpxCredentialSaveFields) => void | Promise<void>;
    readonly onCancel: () => void;
    readonly validateSave?: (payload: IpxCredentialSaveFields) => string | undefined;
    readonly saveLabel?: string;
    readonly isSaving?: boolean;
    readonly saveDisabledReason?: string;
    readonly requireCompleteFields?: boolean;
}

function buildInitialFieldValues(
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[],
    fieldValues: Readonly<Record<string, string>>
): Record<string, string> {
    const normalizedFieldValues: Record<string, string> = {};
    for (const descriptor of fieldDescriptors) {
        normalizedFieldValues[descriptor.field_name] = fieldValues[descriptor.field_name] ?? '';
    }
    return normalizedFieldValues;
}

function hasFieldValueChanges(
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[],
    initialFieldValues: Readonly<Record<string, string>>,
    currentFieldValues: Readonly<Record<string, string>>
): boolean {
    for (const descriptor of fieldDescriptors) {
        if (
            initialFieldValues[descriptor.field_name] !== currentFieldValues[descriptor.field_name]
        ) {
            return true;
        }
    }
    return false;
}

function buildEditedFields(
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[],
    initialFieldValues: Readonly<Record<string, string>>,
    currentFieldValues: Readonly<Record<string, string>>
): Record<string, string> {
    const editedFields: Record<string, string> = {};
    for (const descriptor of fieldDescriptors) {
        const fieldName: string = descriptor.field_name;
        if (initialFieldValues[fieldName] !== currentFieldValues[fieldName]) {
            editedFields[fieldName] = currentFieldValues[fieldName] ?? '';
        }
    }
    return editedFields;
}

function getSaveDisabledReason(params: {
    readonly isDirty: boolean;
    readonly isSaving: boolean;
    readonly externalReason?: string;
    readonly credentialFieldReason?: string;
    readonly saveValidationReason?: string;
}): string | undefined {
    if (params.isSaving) {
        return 'Saving credential...';
    }
    if (params.externalReason !== undefined) {
        return params.externalReason;
    }
    if (!params.isDirty) {
        return 'Edit at least one field before saving';
    }
    if (params.credentialFieldReason !== undefined) {
        return params.credentialFieldReason;
    }
    if (params.saveValidationReason !== undefined) {
        return params.saveValidationReason;
    }
    return undefined;
}

function getCredentialFieldInputType(
    descriptor: IpxCredentialFieldDescriptor
): 'password' | 'text' {
    return descriptor.field_classification === IpxCredentialFieldClassification.SECRET
        ? 'password'
        : 'text';
}

function validateRequiredCredentialFields(params: {
    readonly fieldDescriptors: readonly IpxCredentialFieldDescriptor[];
    readonly initialFieldValues: Readonly<Record<string, string>>;
    readonly currentFieldValues: Readonly<Record<string, string>>;
    readonly requireCompleteFields: boolean;
}): string | undefined {
    for (const descriptor of params.fieldDescriptors) {
        if (!descriptor.required) {
            continue;
        }
        const fieldName = descriptor.field_name;
        const currentValue = params.currentFieldValues[fieldName] ?? '';
        const initialValue = params.initialFieldValues[fieldName] ?? '';
        const fieldChanged = currentValue !== initialValue;
        if (currentValue.trim().length === 0 && (params.requireCompleteFields || fieldChanged)) {
            return `${descriptor.display.label} is required.`;
        }
    }
    return undefined;
}

export function IpxCredentialForm(props: IpxCredentialFormProps): ReactElement {
    const formId: string = useId();
    const [confirmCancelOpen, setConfirmCancelOpen] = useState<boolean>(false);
    const initialFieldValues: Record<string, string> = useMemo(
        () => buildInitialFieldValues(props.fieldDescriptors, props.initialValues.field_values),
        [props.fieldDescriptors, props.initialValues.field_values]
    );
    const [accountId, setAccountId] = useState<string>(props.initialValues.account_id);
    const [fieldValues, setFieldValues] = useState<Record<string, string>>(initialFieldValues);
    useEffect(() => {
        setAccountId(props.initialValues.account_id);
        setFieldValues(initialFieldValues);
    }, [initialFieldValues, props.initialValues.account_id]);

    const hasCredentialFieldChanges: boolean = hasFieldValueChanges(
        props.fieldDescriptors,
        initialFieldValues,
        fieldValues
    );
    const isDirty: boolean =
        accountId !== props.initialValues.account_id || hasCredentialFieldChanges;
    const credentialFieldDisabledReason: string | undefined = validateRequiredCredentialFields({
        fieldDescriptors: props.fieldDescriptors,
        initialFieldValues,
        currentFieldValues: fieldValues,
        requireCompleteFields: props.requireCompleteFields === true,
    });
    const savePayload: IpxCredentialSaveFields = {
        account_id: accountId,
        edited_fields: buildEditedFields(props.fieldDescriptors, initialFieldValues, fieldValues),
    };
    const saveDisabledReason: string | undefined = getSaveDisabledReason({
        isDirty,
        isSaving: props.isSaving === true,
        externalReason: props.saveDisabledReason,
        credentialFieldReason: credentialFieldDisabledReason,
        saveValidationReason: props.validateSave?.(savePayload),
    });
    const saveDisabled: boolean = saveDisabledReason !== undefined;

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (saveDisabled) {
            return;
        }
        runInBackground(Promise.resolve(props.onSave(savePayload)), () => undefined);
    };

    const handleCancel = (): void => {
        if (isDirty) {
            setConfirmCancelOpen(true);
            return;
        }
        props.onCancel();
    };

    return (
        <>
            <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-2">
                    <Label htmlFor={`${formId}-account-id`}>Account ID</Label>
                    <Input
                        id={`${formId}-account-id`}
                        value={accountId}
                        onChange={(event) => {
                            setAccountId(event.target.value);
                        }}
                    />
                </div>

                {props.fieldDescriptors.map((descriptor) => {
                    const inputId: string = `${formId}-${descriptor.field_name}`;
                    return (
                        <div key={descriptor.field_name} className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Label htmlFor={inputId}>{descriptor.display.label}</Label>
                                {descriptor.required ? (
                                    <span className="text-xs text-muted-foreground">Required</span>
                                ) : null}
                            </div>
                            <Input
                                id={inputId}
                                type={getCredentialFieldInputType(descriptor)}
                                value={fieldValues[descriptor.field_name] ?? ''}
                                onChange={(event) => {
                                    const nextFieldValue: string = event.target.value;
                                    setFieldValues((currentFieldValues) => ({
                                        ...currentFieldValues,
                                        [descriptor.field_name]: nextFieldValue,
                                    }));
                                }}
                            />
                        </div>
                    );
                })}

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={saveDisabled} title={saveDisabledReason}>
                        {props.isSaving === true ? 'Saving...' : (props.saveLabel ?? 'Save')}
                    </Button>
                </div>
            </form>
            <ConfirmationModal
                open={confirmCancelOpen}
                onOpenChange={setConfirmCancelOpen}
                title="Discard edits?"
                message="Canceling will discard unsaved credential edits."
                confirmLabel="Discard edits"
                onConfirm={() => {
                    setConfirmCancelOpen(false);
                    props.onCancel();
                }}
                onCancel={() => {
                    setConfirmCancelOpen(false);
                }}
            />
        </>
    );
}
